import { useState, useEffect, useCallback } from "react";

// ─── Severity config ──────────────────────────────────────────────────────────
const SEV = {
  critical: { color: "#a855f7", bg: "#2d1b4e", label: "CRITICAL", icon: "🚨" },
  high:     { color: "#ef4444", bg: "#3b1414", label: "HIGH",     icon: "🔴" },
  medium:   { color: "#f97316", bg: "#3b2008", label: "MEDIUM",   icon: "🟡" },
  low:      { color: "#3b82f6", bg: "#0f2744", label: "LOW",      icon: "🔵" },
  info:     { color: "#22c55e", bg: "#0a2e14", label: "INFO",     icon: "✅" },
};

const TYPE_LABELS = {
  SQLI_ATTACK:          "SQL Injection",
  XSS_ATTACK:           "Cross-Site Scripting",
  PATHTRAVERSAL_ATTACK: "Path Traversal",
  COMMANDINJECTION_ATTACK: "Command Injection",
  SSRF_ATTACK:          "SSRF",
  XXE_ATTACK:           "XXE Injection",
  NOSQLI_ATTACK:        "NoSQL Injection",
  TEMPLATEINJECTION_ATTACK: "Template Injection",
  HONEYPOT_TRIGGERED:   "Honeypot Hit",
  RATE_LIMIT_EXCEEDED:  "DDoS / Brute Force",
  BLOCKED_IP_RETRY:     "Blocked IP Retry",
  ANOMALY_DETECTED:     "Unknown Pattern",
};

const API = "https://cyberguard-y7il.onrender.com";

function getSev(event) {
  return event.aiAnalysis?.severity || "medium";
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

function formatType(type) {
  return TYPE_LABELS[type] || type?.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

function Pulse({ color }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", width: 10, height: 10 }}>
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: color, opacity: 0.4,
        animation: "ping 1.5s ease-in-out infinite",
      }} />
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
    </span>
  );
}

function StatCard({ label, value, sub, color = "#22c55e", icon }) {
  return (
    <div style={{
      background: "#0d1117", border: "1px solid #1e2d3d",
      borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 130,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2, color: "#4a7fa5", marginBottom: 8, textTransform: "uppercase" }}>
        {icon && <span style={{ marginRight: 6 }}>{icon}</span>}{label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#4a7fa5", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Badge({ sev }) {
  const s = SEV[sev] || SEV.info;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.color}40`,
      padding: "2px 10px", borderRadius: 20, fontSize: 10,
      fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
    }}>
      {s.icon} {s.label}
    </span>
  );
}

function EventRow({ event, onClick, isNew }) {
  const sev = getSev(event);
  const s = SEV[sev] || SEV.medium;
  return (
    <div onClick={() => onClick(event)} style={{
      display: "grid", gridTemplateColumns: "10px 180px 120px 1fr 90px 80px",
      gap: 12, padding: "12px 16px", cursor: "pointer",
      borderBottom: "1px solid #0e1c2a",
      background: isNew ? `${s.color}08` : "transparent",
      transition: "background 0.3s",
      alignItems: "center",
    }}>
      <Pulse color={s.color} />
      <div style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>
        {timeAgo(event.timestamp)}
      </div>
      <div style={{ fontSize: 12, color: "#7dd3fc", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {event.ip}
      </div>
      <div style={{ fontSize: 13, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {formatType(event.type)}
        {event.aiAnalysis?.isNewTechnique && (
          <span style={{ marginLeft: 8, fontSize: 10, color: "#f97316", border: "1px solid #f97316", padding: "1px 6px", borderRadius: 4 }}>
            NEW
          </span>
        )}
      </div>
      <Badge sev={sev} />
      <div style={{ fontSize: 12, color: "#4a7fa5", textAlign: "right", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis" }}>
        {event.path}
      </div>
    </div>
  );
}

function EventDetail({ event, onClose }) {
  if (!event) return null;
  const sev = getSev(event);
  const s = SEV[sev] || SEV.medium;
  const rows = [
    ["Timestamp",  new Date(event.timestamp).toLocaleString()],
    ["IP Address", event.ip],
    ["Path",       event.path],
    ["Method",     event.method],
    ["Action",     event.action],
    ["Strikes",    event.strikes],
    ["Patterns",   (event.patterns || []).join(", ") || "AI-identified"],
    ["User Agent", (event.userAgent || "unknown").slice(0, 100)],
    ...(event.note ? [["Note", event.note]] : []),
  ];

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0d1117", border: `1px solid ${s.color}40`,
        borderRadius: 16, padding: 28, maxWidth: 560, width: "100%",
        maxHeight: "80vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: s.color, marginBottom: 4, textTransform: "uppercase" }}>
              {s.icon} {s.label} SEVERITY
            </div>
            <h2 style={{ margin: 0, fontSize: 18, color: "#e2e8f0" }}>{formatType(event.type)}</h2>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: "#64748b",
            fontSize: 22, cursor: "pointer",
          }}>✕</button>
        </div>

        {event.aiAnalysis && (
          <div style={{ background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: "#4a7fa5", marginBottom: 8, textTransform: "uppercase" }}>🤖 AI Analysis</div>
            <div style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.6, marginBottom: 10 }}>{event.aiAnalysis.explanation}</div>
            <div style={{ fontSize: 13, color: "#7dd3fc" }}>
              <strong style={{ color: "#e2e8f0" }}>Threat:</strong> {event.aiAnalysis.threatType}
              &nbsp;|&nbsp;
              <strong style={{ color: "#e2e8f0" }}>Confidence:</strong> {event.aiAnalysis.confidence}%
              &nbsp;|&nbsp;
              <strong style={{ color: "#e2e8f0" }}>Action:</strong> {event.aiAnalysis.recommendation}
            </div>
            {event.aiAnalysis.isNewTechnique && (
              <div style={{ marginTop: 10, padding: "6px 12px", background: "#3b2008", border: "1px solid #f97316", borderRadius: 6, fontSize: 12, color: "#f97316" }}>
                ⚠️ New or unusual attack technique detected.
              </div>
            )}
          </div>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: "1px solid #0e1c2a" }}>
              <td style={{ padding: "8px 0", color: "#4a7fa5", width: 120, verticalAlign: "top" }}>{k}</td>
              <td style={{ padding: "8px 0", color: "#94a3b8", wordBreak: "break-all" }}>{String(v || "—")}</td>
            </tr>
          ))}
        </table>
      </div>
    </div>
  );
}

function BarChart({ data }) {
  if (!data || !Object.keys(data).length) return (
    <div style={{ color: "#4a7fa5", fontSize: 13, padding: 20, textAlign: "center" }}>No threats yet — system is clean ✅</div>
  );
  const max = Math.max(...Object.values(data));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
      {Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([type, count]) => (
        <div key={type} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#64748b", width: 180, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {formatType(type)}
          </div>
          <div style={{ flex: 1, background: "#0e1c2a", borderRadius: 4, height: 8 }}>
            <div style={{
              width: `${(count / max) * 100}%`, height: 8,
              background: "linear-gradient(90deg, #3b82f6, #a855f7)",
              borderRadius: 4, transition: "width 0.6s ease",
            }} />
          </div>
          <div style={{ fontSize: 12, color: "#7dd3fc", minWidth: 24, textAlign: "right" }}>{count}</div>
        </div>
      ))}
    </div>
  );
}

export default function CyberGuardDashboard() {
  const [events,   setEvents]   = useState([]);
  const [stats,    setStats]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [newIds,   setNewIds]   = useState(new Set());
  const [connected, setConnected] = useState(false);
  const [filter,   setFilter]   = useState("all");

  const loadData = useCallback(async () => {
    try {
      const [eRes, sRes] = await Promise.all([
        fetch(`${API}/cyberguard/events`),
        fetch(`${API}/cyberguard/stats`),
      ]);
      if (eRes.ok) {
        const data = await eRes.json();
        const newEvents = data.events || [];
        setEvents(prev => {
          const existingIds = new Set(prev.map(e => e.id));
          const brandNew = newEvents.filter(e => !existingIds.has(e.id));
          if (brandNew.length > 0) {
            const newIdSet = new Set(brandNew.map(e => e.id));
            setNewIds(newIdSet);
            setTimeout(() => setNewIds(new Set()), 3000);
          }
          return newEvents;
        });
      }
      if (sRes.ok) setStats(await sRes.json());
      setConnected(true);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, [loadData]);

  const filtered = events.filter(e =>
    filter === "all"      ? true :
    filter === "critical" ? getSev(e) === "critical" :
    filter === "high"     ? getSev(e) === "high" :
    filter === "honeypot" ? e.type === "HONEYPOT_TRIGGERED" :
    filter === "new"      ? e.aiAnalysis?.isNewTechnique : true
  );

  return (
    <div style={{ background: "#060b14", minHeight: "100vh", color: "#e2e8f0", fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <style>{`
        @keyframes ping {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.8); opacity: 0; }
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0d1117; }
        ::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 3px; }
      `}</style>

      {/* Header */}
      <div style={{
        borderBottom: "1px solid #0e1c2a", padding: "14px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#080e1a",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 22 }}>🛡️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>CyberGuard AI</div>
            <div style={{ fontSize: 11, color: "#4a7fa5", letterSpacing: 2, textTransform: "uppercase" }}>Security Operations Center</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <Pulse color={connected ? "#22c55e" : "#ef4444"} />
          <span style={{ color: connected ? "#22c55e" : "#ef4444" }}>
            {connected ? "Live — connected" : "Connecting..."}
          </span>
        </div>
      </div>

      <div style={{ padding: "24px 28px" }}>
        {/* Stat cards */}
        <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard label="Total blocked"  value={stats?.totalBlocked ?? "—"} color="#ef4444" icon="🚫" />
          <StatCard label="Last 24 hours"  value={stats?.last24h ?? "—"}       color="#f97316" icon="📊" sub="threats detected" />
          <StatCard label="IPs banned now" value={stats?.activeBlocks ?? "—"}  color="#a855f7" icon="🔒" />
          <StatCard label="Honeypot hits"  value={stats?.honeypotHits ?? "—"}  color="#eab308" icon="🍯" sub="attacker traps triggered" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
          {/* Live feed */}
          <div style={{ background: "#0d1117", border: "1px solid #1e2d3d", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #0e1c2a", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                Live Threat Feed
                {events.length > 0 && <span style={{ marginLeft: 10, fontSize: 11, color: "#4a7fa5" }}>{events.length} events</span>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {["all", "critical", "high", "honeypot", "new"].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{
                    background: filter === f ? "#1e3a5f" : "transparent",
                    border: "1px solid " + (filter === f ? "#3b82f6" : "#1e2d3d"),
                    color: filter === f ? "#7dd3fc" : "#64748b",
                    padding: "4px 10px", borderRadius: 6, fontSize: 11,
                    cursor: "pointer", textTransform: "capitalize",
                  }}>
                    {f === "new" ? "New tech" : f}
                  </button>
                ))}
              </div>
            </div>

            <div style={{
              display: "grid", gridTemplateColumns: "10px 180px 120px 1fr 90px 80px",
              gap: 12, padding: "8px 16px",
              fontSize: 10, letterSpacing: 2, color: "#2a4a6a", textTransform: "uppercase",
              borderBottom: "1px solid #0e1c2a",
            }}>
              <div /><div>Time</div><div>IP Address</div><div>Threat type</div><div>Severity</div><div>Path</div>
            </div>

            <div style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "#4a7fa5", fontSize: 14 }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🛡️</div>
                  <div>{events.length === 0 ? "No threats detected. Your apps are clean." : "No threats match this filter."}</div>
                  {events.length === 0 && <div style={{ fontSize: 12, marginTop: 8, color: "#2a4a6a" }}>Refreshing every 3 seconds...</div>}
                </div>
              ) : (
                filtered.map(event => (
                  <EventRow key={event.id} event={event} onClick={setSelected} isNew={newIds.has(event.id)} />
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#0d1117", border: "1px solid #1e2d3d", borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#4a7fa5", marginBottom: 14, textTransform: "uppercase" }}>Attack breakdown</div>
              <BarChart data={stats?.byType} />
            </div>

            <div style={{ background: "#0d1117", border: "1px solid #1e2d3d", borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#4a7fa5", marginBottom: 14, textTransform: "uppercase" }}>Top attacking IPs</div>
              {(stats?.topIPs || []).length === 0 ? (
                <div style={{ fontSize: 13, color: "#2a4a6a" }}>No data yet</div>
              ) : (
                (stats?.topIPs || []).map(({ ip, count }, i) => (
                  <div key={ip} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #0e1c2a", fontSize: 13 }}>
                    <span style={{ color: "#7dd3fc", fontFamily: "monospace", fontSize: 12 }}>
                      <span style={{ color: "#2a4a6a", marginRight: 8 }}>#{i + 1}</span>{ip}
                    </span>
                    <span style={{ color: "#ef4444", fontWeight: 600 }}>{count}</span>
                  </div>
                ))
              )}
            </div>

            <div style={{ background: "#0d1117", border: "1px solid #1e2d3d", borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#4a7fa5", marginBottom: 14, textTransform: "uppercase" }}>Alert channels</div>
              {[
                { label: "Gmail alerts",   icon: "📧", active: true },
                { label: "Live dashboard", icon: "📺", active: true },
                { label: "AI classifier",  icon: "🤖", active: true },
                { label: "Honeypot traps", icon: "🍯", active: true },
                { label: "Tarpit mode",    icon: "🐢", active: true },
              ].map(({ label, icon, active }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #0e1c2a", fontSize: 13 }}>
                  <span style={{ color: "#94a3b8" }}>{icon} {label}</span>
                  <span style={{ fontSize: 10, color: active ? "#22c55e" : "#64748b", background: active ? "#0a2e14" : "#1a1a1a", padding: "2px 8px", borderRadius: 10, border: `1px solid ${active ? "#22c55e40" : "#333"}` }}>
                    {active ? "ACTIVE" : "OFF"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selected && <EventDetail event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
